import {
  DECIMAL_FORMAT_THOUSANDS_COMMA_DECIMAL_DOT,
  DECIMAL_FORMAT_THOUSANDS_DOT_DECIMAL_COMMA,
  PATTERN_THOUSANDS_COMMA,
  PATTERN_THOUSANDS_DOT,
} from 'sling-calendar-sdk/constants';

export default function Utils(
  AlertService,
  sdk,
  $injector,
  $sce,
  $rootScope,
  $timeout,
  $q,
  $window
) {
  'ngInject';

  const service = this;
  const _utils = sdk('utils');

  Object.assign(service, _utils);

  service.getErrorMessages = getErrorMessages;
  service.evalAsyncOnce = evalAsyncOnce;
  service.memoizePerDigest = memoizePerDigest;
  service.debounce = debounce;
  service.throttle = throttle;
  service.getCoordinates = getCoordinates;
  service.getCountryCodeData = getCountryCodeData;
  service.getGoogleAnalyticsData = getGoogleAnalyticsData;
  service.getDecimalFormat = getDecimalFormat;
  service.getNumberValidationPattern = getNumberValidationPattern;

  service.getDaysForPeriod = (...args) => {
    const {
      firstDayOfWeek,
      firstDayOfMonth,
      payrollPeriodType,
      payrollPeriodStartDate,
      payrollPeriodStartDateTwo,
    } = $injector.get('Authentication').user.orgSettings;

    return _utils.getDaysForPeriod(...args, firstDayOfWeek, firstDayOfMonth, {
      payrollPeriodType,
      payrollPeriodStartDate,
      payrollPeriodStartDateTwo,
    });
  };
  service.parseTimeString = (...args) =>
    _utils.parseTimeString(...args, $injector.get('Authentication').user.settings.usTimeFormat);
  service.getShiftHourOptions = (...args) =>
    _utils.getShiftHourOptions(...args, $injector.get('Authentication').user.settings.usTimeFormat);
  service.evaluateExpression = (exp, ctx) =>
    _utils.evaluateExpression(exp, {
      CURRENCY_SYMBOL: $rootScope.currencySymbol,
      SALARIES_ENABLED: true,
      ...(ctx || {}),
    });

  /**
   * Same contract as the SDK's `raceProofRequest` — resolve only while this is
   * the most recent promise registered under `id` — but the slot is released
   * once the request settles.
   *
   * The SDK keeps its entries for the life of the page, which was harmless when
   * every caller shared one 'default' slot. Now that reports key per range
   * (RF-35753), that would retain one settled response per range visited, so the
   * registry is kept here instead.
   */
  const raceProofRequestPromises = new Map();

  // Exposed so the release behaviour above can be asserted directly.
  service.getRaceProofRequestSlotCount = () => raceProofRequestPromises.size;

  service.raceProofRequest = async (promise, id = 'default') => {
    raceProofRequestPromises.set(id, promise);

    try {
      const result = await promise;

      if (raceProofRequestPromises.get(id) !== promise) {
        throw 'Stale promise resolved';
      }

      return result;
    } finally {
      // Only the newest registration owns the slot, so a superseded request must
      // leave it in place for the one that replaced it.
      if (raceProofRequestPromises.get(id) === promise) {
        raceProofRequestPromises.delete(id);
      }
    }
  };

  function getDecimalFormat() {
    const Authentication = $injector.get('Authentication');
    const CurrencyService = $injector.get('CurrencyService');
    if (Authentication.isOrgFeatureEnabled('laborCost')) {
      const orgCurrency = CurrencyService.getOrgCurrency();

      if (orgCurrency?.decimalFormat) {
        return orgCurrency.decimalFormat;
      }
    }

    return Authentication.user.settings.usTimeFormat
      ? DECIMAL_FORMAT_THOUSANDS_COMMA_DECIMAL_DOT
      : DECIMAL_FORMAT_THOUSANDS_DOT_DECIMAL_COMMA;
  }

  function getNumberValidationPattern() {
    const decimalFormat = getDecimalFormat();

    return decimalFormat === DECIMAL_FORMAT_THOUSANDS_COMMA_DECIMAL_DOT
      ? PATTERN_THOUSANDS_COMMA
      : PATTERN_THOUSANDS_DOT;
  }

  function getErrorMessages(error, errorsDict = {}) {
    let messages = [];
    const message = error.data?.message ?? error.statusText;

    if (message) {
      messages = [message];
    }

    if (error?.status in errorsDict) {
      messages = [errorsDict[error.status]];
    }

    if (error.data?.errors) {
      messages = error.data.errors;
    }

    if (message && typeof message === 'object') {
      messages = Object.values(message);
    }

    return messages.length ? messages : null;
  }

  // ensure fn is called only once per digest cycle
  // handy if fn is a function passed to multiple watchers
  // and all of them run initially
  function evalAsyncOnce(fn) {
    // if we have this funciton already in cache just do nothing
    if (!evalAsyncOnce.cache.includes(fn)) {
      evalAsyncOnce.cache.push(fn);

      // schedule to run it (once) after this digest
      $rootScope.$$postDigest(() => {
        $rootScope.$evalAsync(fn);
        // remove it from cache - allows to run it again after next digest
        const index = evalAsyncOnce.cache.indexOf(fn);
        evalAsyncOnce.cache.splice(index, 1);
      });
    }
  }
  evalAsyncOnce.cache = [];

  function debounce(fn, time, ...args) {
    if (fn._timer) {
      $timeout.cancel(fn._timer);
    }

    fn._timer = $timeout(fn, time, true, ...args);
    return fn._timer;
  }

  function throttle(fn, time) {
    let inThrottle = false;
    return function throttledFunction(...args) {
      if (!inThrottle) {
        inThrottle = true;
        fn(...args);
        $timeout(() => (inThrottle = false), time);
      }
    };
  }

  async function getCoordinates() {
    if (!$window.navigator.geolocation) {
      return {};
    }

    try {
      const geo = await $q((resolve, reject) => {
        $window.navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
        });
      });

      return geo.coords;
    } catch (e) {
      if (!__TEST__) {
        console.warn(e);
      }

      return {};
    }
  }

  function memoizePerDigest(fn) {
    const key = fn.toString();
    if (!(key in memoizePerDigest.cache)) {
      memoizePerDigest.cache[key] = fn();
      $rootScope.$$postDigest(() => {
        delete memoizePerDigest.cache[key];
      });
    }

    return memoizePerDigest.cache[key];
  }

  memoizePerDigest.cache = {};

  function getCountryCodeData(countryCode, countryIsoCode, geo, user, countries) {
    if (!countryCode) {
      if (user?.countryCode) {
        const { countryCode: _countryCode, countryISOCode: _countryISOCode } = user;
        return _utils.getCountryEntry(_countryCode, _countryISOCode, countries);
      }

      if (geo?.countryCode) {
        return _utils.getCountryEntry(geo.countryCode, geo.country, countries);
      }

      return _utils.getCountryEntry(null, null, countries);
    }

    return _utils.getCountryEntry(countryCode, countryIsoCode, countries);
  }

  function getGoogleAnalyticsData() {
    if (!$window.ga || !$window.ga.getAll) {
      return {};
    }

    const tracker = $window.ga.getAll()[0];
    const gaParamNames = [
      'clientId',
      'referrer',
      'campaignName',
      'campaignSource',
      'campaignMedium',
      'campaignKeyword',
      'campaignContent',
      'campaignId',
      'language',
    ];

    const params = {};
    gaParamNames.forEach((paramName) => {
      params[paramName] = tracker.get(paramName);
    });

    return params;
  }
}
